import React, { useMemo, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useGetLayoutByIdQuery, DashboardWidget } from '../../../redux/wallboardApi';
import { useDashboardContext } from '../pages/DashboardViewPage';

/**
 * Widget Grid
 * 
 * Renders widgets in a grid layout according to the dashboard layout configuration.
 * Automatically scales to fill the screen in fullscreen mode.
 */
interface WidgetGridProps {
  layoutId?: string;
  children: React.ReactNode;
}

export const WidgetGrid: React.FC<WidgetGridProps> = ({
  layoutId,
  children,
}) => {
  const { data: layout } = useGetLayoutByIdQuery(layoutId!, { skip: !layoutId });
  const { isFullscreen } = useDashboardContext();
  const [containerHeight, setContainerHeight] = useState(0);

  // Default grid configuration
  const gridConfig = layout?.gridDefinition || {
    columns: 12,
    rows: 6,
    gap: 16,
    rowHeight: 100,
  };

  // Convert children to array
  const childArray = React.Children.toArray(children);

  // Calculate the maximum row extent from widget positions
  const maxRowExtent = useMemo(() => {
    let maxRow = gridConfig.rows;
    childArray.forEach((child) => {
      const widget = (child as any)?.props?.widget as DashboardWidget | undefined;
      const position = widget?.position;
      if (position) {
        const endRow = position.y + position.height;
        if (endRow > maxRow) maxRow = endRow;
      }
    });
    return maxRow;
  }, [childArray, gridConfig.rows]);

  // Update container height on resize for fullscreen calculations
  useEffect(() => {
    if (!isFullscreen) return;
    
    const updateHeight = () => {
      // Account for header (~80px) and controls (~60px) in fullscreen
      const availableHeight = window.innerHeight - 160;
      setContainerHeight(availableHeight);
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [isFullscreen]);

  // Calculate dynamic row height in fullscreen mode
  const dynamicRowHeight = useMemo(() => {
    if (!isFullscreen || containerHeight === 0) {
      return gridConfig.rowHeight;
    }
    // Calculate row height to fit all rows in available space
    const totalGapHeight = (maxRowExtent - 1) * gridConfig.gap;
    const availableForRows = containerHeight - totalGapHeight;
    const calculatedHeight = Math.floor(availableForRows / maxRowExtent);
    // Ensure minimum row height of 80px
    return Math.max(80, calculatedHeight);
  }, [isFullscreen, containerHeight, maxRowExtent, gridConfig.gap, gridConfig.rowHeight]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${gridConfig.columns}, 1fr)`,
        gridAutoRows: `${dynamicRowHeight}px`,
        gap: `${gridConfig.gap}px`,
        width: '100%',
        minHeight: isFullscreen ? containerHeight : 400,
        height: isFullscreen ? containerHeight : 'auto',
      }}
    >
      {childArray.map((child, index) => {
        // Get widget position from child props if available
        const widget = (child as any)?.props?.widget as DashboardWidget | undefined;
        const position = widget?.position;

        if (position) {
          return (
            <Box
              key={widget?.id || index}
              sx={{
                gridColumn: `${position.x + 1} / span ${position.width}`,
                gridRow: `${position.y + 1} / span ${position.height}`,
              }}
            >
              {child}
            </Box>
          );
        }

        // Default positioning for widgets without position
        return (
          <Box
            key={index}
            sx={{
              gridColumn: 'span 4',
              gridRow: 'span 2',
            }}
          >
            {child}
          </Box>
        );
      })}
    </Box>
  );
};

export default WidgetGrid;
